import React, { useState } from 'react';
import { RadioGroup } from '@headlessui/react';
import { Checkbox, Radio } from 'antd';
import { Tooltip } from 'react-tooltip';
import storeApi from '../../../../services/storeApi';
import {
  SectionHeading,
  ToggleRow,
  primaryButtonClass,
} from '../components/settings-ui';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

const tipRateOptions = [
  { key: 'tiprate1' },
  { key: 'tiprate2' },
  { key: 'tiprate3' },
  { key: 'tiprate4' },
];

const autoAcceptDurations = [
  { name: '10 min', value: 10 },
  { name: '15 min', value: 15 },
  { name: '20 min', value: 20 },
  { name: '25 min', value: 25 },
  { name: '30+ min', value: 30 },
];

const OrderTab = ({ storeDetail, checkUpdate }) => {
  const isNewStore =
    storeDetail.tiprate1 === 0 &&
    storeDetail.tiprate2 === 0 &&
    storeDetail.tiprate3 === 0 &&
    storeDetail.tiprate4 === 0;

  const [switches, setSwitches] = useState({
    customerfeeactive: storeDetail.customerfeeactive,
    customerfeeisrate: storeDetail.customerfeeisrate,
    tipactive: storeDetail.tipactive,
    customernoteactive: storeDetail.customernoteactive,
    customernameactive: storeDetail.customernameactive,
    tablenumberactive: storeDetail.tablenumberactive,
    tablenumberrequire: storeDetail.tablenumberrequire,
    customerfee: storeDetail.customerfee,
    selectedtip: storeDetail.selectedtip,
    autocompleteorder: storeDetail.autocompleteorder,
    autoacceptordermin: storeDetail.autoacceptordermin,
    tiprate1: isNewStore ? 25 : storeDetail.tiprate1,
    tiprate2: isNewStore ? 20 : storeDetail.tiprate2,
    tiprate3: isNewStore ? 15 : storeDetail.tiprate3,
    tiprate4: isNewStore ? 10 : storeDetail.tiprate4,
    tiprate5: storeDetail.tiprate5,
  });

  const [loading, setLoading] = useState(false);

  const onChangeTipRate = (key, val) =>
    setSwitches((prev) => ({ ...prev, [key]: val }));

  const onChangeTipType = (val) =>
    setSwitches((prev) => ({
      ...prev,
      selectedtip: prev.selectedtip === val ? 0 : val,
    }));

  const onFormSubmit = async () => {
    try {
      setLoading(true);
      const body = {
        ...storeDetail,
        ...switches,
        websiteclosedat: storeDetail.websiteclosedat || '',
      };

      const formData = new FormData();
      for (const key of Object.keys(body)) {
        if (body[key] && body[key] !== 'null') {
          formData.append(key, body[key]);
        }
      }

      const config = {
        headers: { 'Content-Type': 'multipart/form-data' },
      };

      const res = await storeApi.upsert(formData, config);
      if (res.status === 200) {
        checkUpdate(true);
      }
    } catch (error) {
      checkUpdate(false);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <SectionHeading
        title="Order Settings"
        description="Configure how customers place orders and how the kitchen processes them."
      />

      <div className="space-y-4">
        <ToggleRow
          title="Customer note"
          description='Allow customers to add item notes (e.g. "No onions, please").'
          enabled={switches.customernoteactive}
          onChange={() =>
            setSwitches((prev) => ({
              ...prev,
              customernoteactive: !prev.customernoteactive,
            }))
          }
        />

        <ToggleRow
          title="Tipping"
          description="Present preset tip percentages at checkout. Customers can still adjust or remove the tip before paying."
          enabled={switches.tipactive}
          onChange={() =>
            setSwitches((prev) => ({ ...prev, tipactive: !prev.tipactive }))
          }
        >
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            {tipRateOptions.map((x) => {
              const isSelected = switches.selectedtip === switches[x.key];
              return (
                <div
                  key={x.key}
                  className={classNames(
                    'flex items-center gap-3 rounded-md border px-3 py-2.5 transition',
                    isSelected
                      ? 'border-blue-500 bg-blue-50/70 dark:border-sky-400 dark:bg-sky-400/10'
                      : 'border-slate-200 bg-white dark:border-slate-700 dark:bg-slate-900/60',
                  )}
                >
                  <Radio
                    onClick={() => onChangeTipType(switches[x.key])}
                    checked={isSelected}
                  />
                  <div className="relative flex flex-1 items-center">
                    <span className="pointer-events-none absolute left-3 text-sm text-slate-400 dark:text-slate-500">
                      %
                    </span>
                    <input
                      onChange={(e) => onChangeTipRate(x.key, e.target.value)}
                      type="number"
                      value={switches[x.key]}
                      className="w-full rounded-md border border-transparent bg-transparent py-1.5 pl-6 pr-2 text-sm font-semibold tabular-nums text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-400/20 dark:text-slate-100 dark:focus:border-sky-400 dark:focus:ring-sky-400/20"
                      placeholder="0"
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </ToggleRow>

        <ToggleRow
          title="Auto complete"
          description='Automatically complete orders once they are marked "Ready".'
          enabled={switches.autocompleteorder}
          onChange={() =>
            setSwitches((prev) => ({
              ...prev,
              autocompleteorder: !prev.autocompleteorder,
            }))
          }
        />

        <ToggleRow
          title="Auto accept orders"
          description="Automatically accept new incoming orders and set a preparation time."
          enabled={switches.autoacceptordermin > 0}
          onChange={() =>
            setSwitches((prev) => ({
              ...prev,
              autoacceptordermin:
                prev.autoacceptordermin > 0 ? 0 : autoAcceptDurations[0].value,
            }))
          }
        >
          <RadioGroup
            value={switches.autoacceptordermin}
            onChange={(val) =>
              setSwitches((prev) => ({
                ...prev,
                autoacceptordermin: Number(val),
              }))
            }
            disabled={!switches.autoacceptordermin}
          >
            <RadioGroup.Label className="sr-only">
              Auto accept preparation time
            </RadioGroup.Label>
            <div className="grid grid-cols-3 gap-2 sm:grid-cols-5">
              {autoAcceptDurations.map((option) => (
                <RadioGroup.Option
                  key={option.name}
                  value={option.value}
                  disabled={!switches.autoacceptordermin}
                  className={({ checked }) =>
                    classNames(
                      'flex cursor-pointer items-center justify-center rounded-full border px-3 py-2 text-xs font-semibold transition',
                      checked
                        ? 'border-transparent bg-blue-600 text-white shadow-sm dark:bg-sky-500'
                        : 'border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900/60 dark:text-slate-200 dark:hover:bg-slate-800',
                      !switches.autoacceptordermin &&
                        'cursor-not-allowed opacity-50',
                    )
                  }
                >
                  <RadioGroup.Label as="span">{option.name}</RadioGroup.Label>
                </RadioGroup.Option>
              ))}
            </div>
          </RadioGroup>
        </ToggleRow>

        <ToggleRow
          title="Table number"
          description="Let customers tag their kiosk order with a table number."
          enabled={switches.tablenumberactive}
          onChange={() =>
            setSwitches((prev) => ({
              ...prev,
              tablenumberactive: !prev.tablenumberactive,
            }))
          }
          trailing={
            <div className="hidden items-center gap-2 sm:flex">
              <Tooltip id="tablenumberrequire" />
              <Checkbox
                data-tooltip-id="tablenumberrequire"
                data-tooltip-html="Require Table Number"
                checked={switches.tablenumberrequire}
                disabled={!switches.tablenumberactive}
                onChange={() =>
                  setSwitches((prev) => ({
                    ...prev,
                    tablenumberrequire: !prev.tablenumberrequire,
                  }))
                }
              />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400">
                Required
              </span>
            </div>
          }
        >
          <div className="flex items-center gap-2 sm:hidden">
            <Checkbox
              checked={switches.tablenumberrequire}
              disabled={!switches.tablenumberactive}
              onChange={() =>
                setSwitches((prev) => ({
                  ...prev,
                  tablenumberrequire: !prev.tablenumberrequire,
                }))
              }
            />
            <span className="text-sm font-medium text-slate-700 dark:text-slate-300">
              Require table number at checkout
            </span>
          </div>
        </ToggleRow>
      </div>

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={onFormSubmit}
          disabled={loading}
          className={primaryButtonClass}
        >
          {loading ? 'Saving…' : 'Save changes'}
        </button>
      </div>
    </div>
  );
};

export default OrderTab;
